import * as React from "react";
import { Calendar, Eye, MessageSquare, MoreVertical, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useToggle } from "react-use";
import { ModifySnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/modifySnapshotEventDialog";
import { DeleteSnapshotsDialog } from "@/components/snapshots/DeleteSnapshotsDialog";
import { useDeleteBusinessReportOutcomeMutation } from "@/modules/snapshots/businessReports.api";
import { useDeleteSubcollectionMembersMutation } from "@/modules/snapshots/snapshotCollection.api";
import { toast } from "react-toastify";
import * as analytics from "@/services/analytics";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
  disabled?: boolean;
  disableSnapshotActions?: boolean;
}

export const SnapshotGridTableRowMenu: React.FC<Props> = ({
  businessReportOutcome,
  disabled = false,
  disableSnapshotActions = false,
}) => {
  const navigate = useNavigate();
  const { businessReport, collectionId } = useParams<{ businessReport: string; collectionId: string }>();
  const [searchParams] = useSearchParams();
  const subcollectionId = searchParams.get("subcollection_id") ?? undefined;
  const [isCreateSnapshotEventDialogOpen, toggleIsCreateSnapshotEventDialogOpen] = useToggle(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [deleteBusinessReportOutcome, { isLoading: isDeleting }] = useDeleteBusinessReportOutcomeMutation();
  const [deleteSubcollectionMembers, { isLoading: isRemovingFromSubcollection }] =
    useDeleteSubcollectionMembersMutation();

  const handleClick =
    (url: string, state?: Record<string, string>, allowForFamily = false) =>
    () => {
      if (disabled || (disableSnapshotActions && !allowForFamily)) return;
      navigate(url, state ? { state } : undefined);
    };

  const handleDelete = async () => {
    if (disabled || disableSnapshotActions) return;
    try {
      if (subcollectionId) {
        await deleteSubcollectionMembers({
          id: subcollectionId,
          business_report_outcome_ids: [businessReportOutcome.id],
        }).unwrap();
        toast.success("Snapshot removed from sub-collection");
      } else {
        await deleteBusinessReportOutcome({ id: businessReportOutcome.id }).unwrap();
        toast.success("Snapshot deleted successfully");
      }
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error(subcollectionId ? "Failed to remove snapshot from sub-collection" : "Failed to delete snapshot");
      console.error("Delete error:", error);
    }
  };

  React.useEffect(() => {
    if (!disabled) return;
    toggleIsCreateSnapshotEventDialogOpen(false);
    setShowDeleteDialog(false);
  }, [disabled, toggleIsCreateSnapshotEventDialogOpen]);

  return (
    <>
      {isCreateSnapshotEventDialogOpen && (
        <ModifySnapshotEventDialog
          mode="create"
          open={isCreateSnapshotEventDialogOpen}
          onClose={toggleIsCreateSnapshotEventDialogOpen}
          businessReportOutcomeResultId={null}
          businessReportOutcomeId={businessReportOutcome.id}
        />
      )}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            size="xs"
            disabled={disabled}
            aria-label={`Snapshot actions for ${businessReportOutcome.name}`}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {businessReportOutcome.fs_entry && (
            <DropdownMenuItem
              onSelect={handleClick(
                `/chat/${businessReportOutcome.fs_entry.id}`,
                {
                  fromSnapshot: `/snapshots/grid/${businessReport}/${collectionId}?outcomeId=${businessReportOutcome.id}`,
                },
                true,
              )}
              className="cursor-pointer"
              disabled={disabled}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              Q&A
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={handleClick(`/reports/outcome/${businessReportOutcome.id}`)}
            className="cursor-pointer"
            disabled={disabled || disableSnapshotActions}
          >
            <Eye className="mr-2 h-4 w-4" />
            View SnapShot
          </DropdownMenuItem>
          {businessReportOutcome.has_snapshot_events && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleClick(
                `/snapshot-events?businessReportOutcomeId=${businessReportOutcome.id}&documentName=`,
                undefined,
                true,
              )}
              disabled={disabled}
            >
              <Calendar className="mr-2 h-4 w-4" />
              View Events
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={() => {
              analytics.snapshotCreateEvent();
              toggleIsCreateSnapshotEventDialogOpen();
            }}
            className="cursor-pointer"
            disabled={disabled || disableSnapshotActions}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Event
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => setShowDeleteDialog(true)}
            className="cursor-pointer text-red-600 hover:text-red-700 focus:text-red-700"
            disabled={disabled || disableSnapshotActions}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {subcollectionId ? "Remove from Sub-collection" : "Delete Snapshot"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteSnapshotsDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        snapshotCount={1}
        snapshotName={businessReportOutcome.name}
        title={subcollectionId ? `Remove "${businessReportOutcome.name}"?` : undefined}
        description={
          subcollectionId
            ? "This will remove the snapshot from this sub-collection only. It will remain in the parent collection and any other sub-collections."
            : undefined
        }
        confirmLabel={subcollectionId ? "Remove" : undefined}
        loadingLabel={subcollectionId ? "Removing..." : undefined}
        onConfirmDelete={handleDelete}
        isDeleting={isDeleting || isRemovingFromSubcollection}
      />
    </>
  );
};
